import { normalizeLiteralEscapeSequences } from '../text/normalize-literal-escape-sequences';

/**
 * Which job body string to show on the job detail page: always prefer the faithful
 * employer extract (`originalDescription`) over the derived rewrite (`description`).
 */

const LOCALE_TRY_ORDER = (locale: string): string[] => {
  const l = locale.toLowerCase();
  const rest = ['en', 'fi', 'sv'].filter((x) => x !== l);
  return [l, ...rest];
};

export type JobDescriptionBodySource = 'original' | 'derived' | 'none';

export type ResolvedJobDescriptionBody = {
  body: string;
  source: JobDescriptionBodySource;
  /**
   * When resolving from suffixed API fields: which locale key supplied `body`
   * (e.g. "en" for originalDescription_en). "unsuffixed" = legacy single field on the record.
   */
  sourceFieldLocale?: string;
};

type DisplayJobLike = {
  originalDescription?: string | null;
  description?: string | null;
};

/**
 * After `transformJobForLanguage`, fields are merged onto base keys.
 */
export function resolveJobDisplayDescriptionFromDisplayJob(job: DisplayJobLike): ResolvedJobDescriptionBody {
  const o = typeof job.originalDescription === 'string' ? job.originalDescription.trim() : '';
  if (o) return { body: normalizeLiteralEscapeSequences(o), source: 'original' };
  const d = typeof job.description === 'string' ? job.description.trim() : '';
  if (d) return { body: normalizeLiteralEscapeSequences(d), source: 'derived' };
  return { body: '', source: 'none' };
}

/**
 * Raw API / Algolia record with per-locale suffix keys (before transform).
 *
 * Checks the page's own locale (original, then legacy, then derived) before ever crossing into
 * another locale's fields. Originally this checked originalDescription_* across ALL locales
 * before looking at description_<pageLocale> — so a job with a real description_en but an empty
 * originalDescription_en (e.g. cleared by fixMislocalizedDescriptionFields upstream because the
 * model's originalDescription_en output was itself in Finnish) would surface originalDescription_fi
 * on the /en/ page: real Finnish prose, silently shown to an English-locale visitor. Only once the
 * page's own locale has nothing at all do we fall back to another locale's text as a last resort.
 */
export function resolveJobDisplayDescriptionFromLocaleRecord(
  job: Record<string, unknown>,
  locale: string,
): ResolvedJobDescriptionBody {
  const [target, ...rest] = LOCALE_TRY_ORDER(locale);

  const targetOriginal = job[`originalDescription_${target}`];
  if (typeof targetOriginal === 'string' && targetOriginal.trim()) {
    return { body: normalizeLiteralEscapeSequences(targetOriginal.trim()), source: 'original', sourceFieldLocale: target };
  }

  const legacyOriginal = job.originalDescription;
  if (typeof legacyOriginal === 'string' && legacyOriginal.trim()) {
    return { body: normalizeLiteralEscapeSequences(legacyOriginal.trim()), source: 'original', sourceFieldLocale: 'unsuffixed' };
  }

  const targetDerived = job[`description_${target}`];
  if (typeof targetDerived === 'string' && targetDerived.trim()) {
    return { body: normalizeLiteralEscapeSequences(targetDerived.trim()), source: 'derived', sourceFieldLocale: target };
  }

  const legacyDerived = job.description;
  if (typeof legacyDerived === 'string' && legacyDerived.trim()) {
    return { body: normalizeLiteralEscapeSequences(legacyDerived.trim()), source: 'derived', sourceFieldLocale: 'unsuffixed' };
  }

  // Nothing in the page's own locale (or legacy fields) — last resort, cross-locale.
  for (const l of rest) {
    const v = job[`originalDescription_${l}`];
    if (typeof v === 'string' && v.trim()) {
      return { body: normalizeLiteralEscapeSequences(v.trim()), source: 'original', sourceFieldLocale: l };
    }
  }
  for (const l of rest) {
    const v = job[`description_${l}`];
    if (typeof v === 'string' && v.trim()) {
      return { body: normalizeLiteralEscapeSequences(v.trim()), source: 'derived', sourceFieldLocale: l };
    }
  }

  return { body: '', source: 'none' };
}

export function pickJobDisplayDescriptionFromDisplayJob(job: DisplayJobLike): string {
  return resolveJobDisplayDescriptionFromDisplayJob(job).body;
}

export function pickJobDisplayDescriptionFromLocaleRecord(job: Record<string, unknown>, locale: string): string {
  return resolveJobDisplayDescriptionFromLocaleRecord(job, locale).body;
}

/**
 * Job detail UI: show which description field is driving the body (dev, or `?describeSource=1` in production).
 */
export function shouldShowJobDescriptionSourceDebug(urlString: string | undefined): boolean {
  if (import.meta.env.DEV) return true;
  if (!urlString) return false;
  try {
    return new URL(urlString, 'http://localhost').searchParams.get('describeSource') === '1';
  } catch {
    return false;
  }
}
